export type PieceType = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export type SpecialKind = 'none' | 'beamH' | 'beamV' | 'nova' | 'prism' | 'burst';

export type ObjectiveKind =
  | 'score'
  | 'clear'
  | 'collect'
  | 'drop'
  | 'blocker'
  | 'spread'
  | 'combo'
  | 'survive';

export type MechanicKind =
  | 'classic'
  | 'layers'
  | 'holes'
  | 'portals'
  | 'conveyor'
  | 'hazard'
  | 'locked'
  | 'multiEntry'
  | 'spawnLimits';

export type BlockerKind = 'none' | 'glass' | 'bramble' | 'wax' | 'lock' | 'meteor';

export interface Coord {
  r: number;
  c: number;
}

export interface CellSpec {
  active?: boolean;
  blocker?: BlockerKind;
  layers?: number;
  locked?: boolean;
  hazard?: number;
  portal?: string;
  conveyor?: 'up' | 'down' | 'left' | 'right';
  spawn?: boolean;
  exit?: boolean;
}

export interface ObjectiveSpec {
  id: string;
  kind: ObjectiveKind;
  target: number;
  label: string;
  pieceType?: PieceType;
  color?: string;
  hint?: string;
}

export interface LevelSpec {
  id: number;
  chapter: number;
  title: string;
  subtitle: string;
  width: number;
  height: number;
  moves: number;
  seed: number;
  palette: string;
  mechanics: MechanicKind[];
  objectives: ObjectiveSpec[];
  cells?: Record<string, CellSpec>;
  tutorial?: string[];
  challenge?: string;
  difficulty: 1 | 2 | 3 | 4 | 5;
}

export interface Piece {
  id: number;
  type: PieceType;
  special: SpecialKind;
  age: number;
  collected?: boolean;
}

export interface CellState extends CellSpec {
  active: boolean;
  blocker: BlockerKind;
  layers: number;
  locked: boolean;
  hazard: number;
  portal?: string;
  conveyor?: 'up' | 'down' | 'left' | 'right';
}

export interface ObjectiveState extends ObjectiveSpec {
  current: number;
  complete: boolean;
}

export interface BoardSnapshot {
  width: number;
  height: number;
  cells: CellState[][];
  pieces: (Piece | null)[][];
  objectives: ObjectiveState[];
  score: number;
  moves: number;
  cascade: number;
  seed: number;
}

export interface MatchGroup {
  type: PieceType;
  cells: Coord[];
  horizontal: boolean;
  vertical: boolean;
  special?: SpecialKind;
  anchor?: Coord;
}

export interface MoveEvent {
  kind:
    | 'swap'
    | 'invalid'
    | 'clear'
    | 'damage'
    | 'special'
    | 'spawn'
    | 'cascade'
    | 'reshuffle'
    | 'objective'
    | 'hazard'
    | 'combo';
  cells?: Coord[];
  from?: Coord;
  to?: Coord;
  special?: SpecialKind;
  label?: string;
  amount?: number;
  cascade?: number;
  color?: string;
}

export interface TurnResult {
  accepted: boolean;
  won: boolean;
  lost: boolean;
  reshuffled: boolean;
  events: MoveEvent[];
  before: BoardSnapshot;
  after: BoardSnapshot;
  validMoves: Coord[][];
  movesRemaining: number;
  scoreGained: number;
}

export interface ProgressRecord {
  unlocked: number;
  stars: Record<number, number>;
  scores: Record<number, number>;
  bestMoves: Record<number, number>;
  chapterSeen: Record<number, boolean>;
}

export const PIECE_NAMES = [
  'Cinderleaf',
  'Mooncap',
  'Gearbud',
  'Prism pear',
  'Coral quill',
  'Inkdrop',
  'Suncoil',
] as const;

export const PIECE_SYMBOLS = ['✦', '◒', '✺', '◇', '❯', '◈', '∿'] as const;

export const PIECE_COLORS = ['#ff6a67', '#6dd9ee', '#ffd36a', '#c493ff', '#ff9b71', '#7195ff', '#69e0b5'] as const;

export const SPECIAL_NAMES: Record<SpecialKind, string> = {
  none: '',
  beamH: 'horizon filament',
  beamV: 'vertical filament',
  nova: 'nova bloom',
  prism: 'prism seed',
  burst: 'orbit knot',
};
